import React, { useState } from 'react';
import { CheckCircle2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { company, serviceNeededOptions } from '../data/site';
import { Reveal } from './Reveal';
import { Button } from './ui/Button';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const fieldClass = 'h-12 w-full rounded-md border border-charcoal-200 bg-white px-4 text-[15px] text-charcoal-900 placeholder:text-charcoal-400 transition-colors duration-150 ease-out focus:border-cobalt-700 focus:outline-none focus:ring-2 focus:ring-cobalt-700/20';
const labelClass = 'mb-2 block text-sm font-semibold text-charcoal-800';
export function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    const data = new FormData(event.currentTarget);
    const value = (key: string) => String(data.get(key) ?? '').trim();
    const subject = `Quote request — ${value('service') || 'Commercial project'}${value('company') ? ` — ${value('company')}` : ''}`;
    const body = [`Name: ${value('name')}`, `Company: ${value('company') || '—'}`, `Phone: ${value('phone')}`, `Email: ${value('email')}`, `Service needed: ${value('service')}`, '', 'Project details:', value('details') || '—'].join('\n');
    window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setStatus('success');
  };
  return <section id="contact" aria-labelledby="contact-heading" className="bg-white py-20 lg:py-28" data-id="element-33" data-mp="2s4x0hbahlx9u">
      <div className="mx-auto max-w-container px-5 lg:px-8" data-id="element-34" data-mp="2w71sk5laelud">
        <Reveal className="max-w-2xl" data-id="element-35" data-mp="qd2380oz9vb5">
          <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-cobalt-700" data-id="element-36" data-mp="qe3h0cxdovwk">
            Contact
          </p>
          <h2 id="contact-heading" className="mt-4 text-3xl font-extrabold leading-[1.12] tracking-tight text-charcoal-900 sm:text-[2.75rem]" data-id="element-37" data-mp="udue0ydi089j">
            Let&rsquo;s Discuss Your Project
          </h2>
          <p className="mt-5 text-lg text-charcoal-600" data-id="element-38" data-mp="1uam9c4sc6znw">
            Share a few details about your commercial or industrial property and
            we&rsquo;ll follow up with the right solution.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.35fr_1fr] lg:gap-14" data-id="element-39" data-mp="2m10hrlhkzjs6">
          <Reveal className="rounded-lg border border-charcoal-200 bg-white p-7 sm:p-9" data-id="element-40" data-mp="2hzyhr2fub3p5">
            {status === 'success' ? <div role="status" className="flex min-h-[360px] flex-col items-start justify-center" data-id="element-41" data-mp="2d8gq54zpifgu">
                <CheckCircle2Icon className="h-11 w-11 text-cobalt-700" aria-hidden="true" data-id="element-42" data-mp="35q2ld9t133g5" />
                <h3 className="mt-5 text-2xl font-extrabold tracking-tight text-charcoal-900" data-id="element-43" data-mp="30ykigg7g2ssj">
                  Your request is ready to send.
                </h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-charcoal-600" data-id="element-44" data-mp="3d9br8t2bsk0g">
                  We&rsquo;ve opened an email with your project details — press
                  send and our team will follow up shortly. If your email app
                  didn&rsquo;t open, call{' '}
                  <a href={company.phoneHref} className="font-semibold text-cobalt-700 underline underline-offset-4" data-id="element-45" data-mp="3r9mkg2r3yaos">
                    {company.phoneDisplay}
                  </a>
                  .
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="mt-7 text-sm font-semibold text-charcoal-600 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-charcoal-900" data-id="element-46" data-mp="4w4kmkicy04s">
                  Submit another request
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate={false} data-id="element-47" data-mp="54g9m75zlc4z">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-48" data-mp="14ft8gzbuqva">
                  <div data-id="element-49" data-mp="ex5j4whz8fxo">
                    <label className={labelClass} htmlFor="name" data-id="element-50" data-mp="evkoz7zkqz0c">
                      Name
                    </label>
                    <input id="name" name="name" type="text" required autoComplete="name" className={fieldClass} placeholder="Your full name" data-id="element-51" data-mp="9y2mo0g2bi1b" />
                  </div>
                  <div data-id="element-52" data-mp="3ncp8hkfj1e7g">
                    <label className={labelClass} htmlFor="company" data-id="element-53" data-mp="3nc5zit7uu05k">
                      Company
                    </label>
                    <input id="company" name="company" type="text" autoComplete="organization" className={fieldClass} placeholder="Company or property name" data-id="element-54" data-mp="17jqb9z6a0w2a" />
                  </div>
                  <div data-id="element-55" data-mp="2bj52bhkwpsj4">
                    <label className={labelClass} htmlFor="phone" data-id="element-56" data-mp="2bjoj2uytmavk">
                      Phone
                    </label>
                    <input id="phone" name="phone" type="tel" required autoComplete="tel" className={fieldClass} placeholder="(000) 000-0000" data-id="element-57" data-mp="2gj173j1fhnzq" />
                  </div>
                  <div data-id="element-58" data-mp="2ug6ky57dvr5j">
                    <label className={labelClass} htmlFor="email" data-id="element-59" data-mp="2zgu7oeokxlty">
                      Email
                    </label>
                    <input id="email" name="email" type="email" required autoComplete="email" className={fieldClass} placeholder="you@company.com" data-id="element-60" data-mp="3n5n9usvsopx0" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-61" data-mp="mts83mx3tl9">
                    <label className={labelClass} htmlFor="service" data-id="element-62" data-mp="3ru672c0bvuy0">
                      Service Needed
                    </label>
                    <select id="service" name="service" required defaultValue="" className={`${fieldClass} appearance-none bg-[length:16px] pr-10`} style={{
                  backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235d6873' stroke-width='2.5' stroke-linecap='round'><path d='M6 9l6 6 6-6'/></svg>\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 14px center'
                }} data-id="element-63" data-mp="3mvuzy91f3yjh">
                      <option value="" disabled data-id="element-64" data-mp="1eohngbbxer7g">
                        Select a service
                      </option>
                      {serviceNeededOptions.map(option => <option key={option} value={option} data-id="element-65" data-mp="1jlhscix1hnwz">
                          {option}
                        </option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-66" data-mp="qx2gg597flpv">
                    <label className={labelClass} htmlFor="details" data-id="element-67" data-mp="vz1klhcrqmnv">
                      Project Details
                    </label>
                    <textarea id="details" name="details" rows={5} className="w-full rounded-md border border-charcoal-200 bg-white px-4 py-3 text-[15px] text-charcoal-900 placeholder:text-charcoal-400 transition-colors duration-150 ease-out focus:border-cobalt-700 focus:outline-none focus:ring-2 focus:ring-cobalt-700/20" placeholder="Property type, scope, square footage, timeline and anything else that helps us understand the project." data-id="element-68" data-mp="10v04arn5jghy" />
                  </div>
                </div>

                <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center" data-id="element-69" data-mp="3bnw6sfx31si3">
                  <Button type="submit" size="lg" disabled={status === 'submitting'} className="w-full sm:w-auto" data-id="element-70" data-mp="3log9ln09352f">
                    {status === 'submitting' ? 'Sending…' : 'Request a Quote'}
                  </Button>
                  <p className="text-sm text-charcoal-500" data-id="element-71" data-mp="3qemvhf3dobgz">
                    Opens your email app with the details filled in, addressed
                    to our team.
                  </p>
                </div>
              </form>}
          </Reveal>

          <Reveal delay={0.08} data-id="element-72" data-mp="38chfrzj8u8b">
            <div className="rounded-lg bg-charcoal-900 p-8" data-id="element-73" data-mp="mvpe6x8lwkhd">
              <h3 className="text-xl font-extrabold tracking-tight text-white" data-id="element-74" data-mp="mu5tmufx3n9s">
                {company.name}
              </h3>
              <dl className="mt-7 space-y-6" data-id="element-75" data-mp="cyb6bm0r140o">
                <div className="flex items-start gap-4" data-id="element-76" data-mp="cxjb4cb2d8ax">
                  <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-cobalt-300" aria-hidden="true" data-id="element-77" data-mp="hwmhc02s5kkh" />
                  <div data-id="element-78" data-mp="43o6r6hrnlwc">
                    <dt className="text-xs font-bold uppercase tracking-[0.14em] text-charcoal-400" data-id="element-79" data-mp="8w7jy2cjkf0m">
                      Location
                    </dt>
                    <dd className="mt-1 text-base font-semibold text-white" data-id="element-80" data-mp="3qesf0pv1p2dj">
                      {company.location}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-81" data-mp="3hdkk8hrqmjo7">
                  <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-cobalt-300" aria-hidden="true" data-id="element-82" data-mp="3ldmkcmhtr51d" />
                  <div data-id="element-83" data-mp="3iorw553w1ijl">
                    <dt className="text-xs font-bold uppercase tracking-[0.14em] text-charcoal-400" data-id="element-84" data-mp="3np64ko9eiui5">
                      Phone
                    </dt>
                    <dd className="mt-1" data-id="element-85" data-mp="39qptym8f4juq">
                      <a href={company.phoneHref} className="text-base font-semibold text-white transition-colors duration-150 ease-out hover:text-cobalt-300" data-id="element-86" data-mp="3dr02o6nlhoda">
                        {company.phoneDisplay}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-87" data-mp="3so0ltlrq0b05">
                  <MailIcon className="mt-0.5 h-5 w-5 shrink-0 text-cobalt-300" aria-hidden="true" data-id="element-88" data-mp="joiux3rq083" />
                  <div data-id="element-89" data-mp="o87qdaezu8z4">
                    <dt className="text-xs font-bold uppercase tracking-[0.14em] text-charcoal-400" data-id="element-90" data-mp="t1ao7uqt04l4">
                      Email
                    </dt>
                    <dd className="mt-1" data-id="element-91" data-mp="2dpd5z7rdpm3e">
                      <a href={company.emailHref} className="break-all text-base font-semibold text-white transition-colors duration-150 ease-out hover:text-cobalt-300" data-id="element-92" data-mp="28smoqh3u8kge">
                        {company.email}
                      </a>
                    </dd>
                  </div>
                </div>
              </dl>
              <p className="mt-8 border-t border-white/10 pt-6 text-sm leading-relaxed text-charcoal-300" data-id="element-93" data-mp="1pth5bkere2xx">
                Commercial painting, industrial cleaning and commercial property
                services throughout Orlando and Central Florida.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}